<script>
export default { name: 'UiTabs' };
</script>

<script setup>
import { computed } from 'vue';

// items: Array de { value: String|Number, label: String, icon?: Componente lucide, badge?: String|Number, disabled?: Boolean }
const props = defineProps({
  modelValue: { type: [String, Number], default: '' },
  items: { type: Array, default: null },
  disabled: { type: Boolean, default: false },
});

const emit = defineEmits(['update:modelValue', 'change']);

const isActive = (item) => props.modelValue === item.value;

const buttonClass = (item) => [
  'relative whitespace-nowrap flex items-center gap-2 px-4 py-2.5 text-sm font-semibold border-b-2 -mb-px transition-colors outline-none cursor-pointer select-none',
  isActive(item)
    ? 'border-brand-500 text-brand-600 dark:border-brand-400 dark:text-brand-400'
    : 'border-transparent text-slate-500 dark:text-slate-400 hover:text-slate-800 dark:hover:text-slate-200 hover:border-slate-300 dark:hover:border-slate-600',
];

const badgeClass = computed(() => [
  'min-w-5 h-5 px-1.5 inline-flex items-center justify-center rounded-full text-[10px] font-bold',
  'bg-brand-100 text-brand-700 dark:bg-brand-500/20 dark:text-brand-300',
]);

const select = (item) => {
  if (props.disabled || item.disabled || isActive(item)) return;
  emit('update:modelValue', item.value);
  emit('change', item.value);
};
</script>

<template>
  <div
    v-if="items && items.length"
    class="border-b border-slate-200 dark:border-slate-800"
    :class="disabled ? 'opacity-60 pointer-events-none' : ''"
  >
    <nav
      class="flex items-center gap-1 overflow-x-auto"
      role="tablist"
      aria-label="Tabs"
    >
      <button
        v-for="(item, index) in items"
        :key="item.value ?? index"
        type="button"
        role="tab"
        :aria-selected="isActive(item)"
        :aria-disabled="item.disabled || disabled"
        :disabled="item.disabled || disabled"
        :class="buttonClass(item)"
        @click="select(item)"
      >
        <component
          :is="item.icon"
          v-if="item.icon"
          class="w-4 h-4"
          aria-hidden="true"
        />
        <span>{{ item.label }}</span>
        <span
          v-if="item.badge !== undefined && item.badge !== null && item.badge !== ''"
          :class="badgeClass"
        >
          {{ item.badge }}
        </span>
      </button>
    </nav>
  </div>
</template>